import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Progress } from '../../ui/progress';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { ScrollArea } from '../../ui/scroll-area';
import { Separator } from '../../ui/separator';
import { Avatar, AvatarFallback } from '../../ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../../ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { 
  Target, 
  Loader2, 
  Save, 
  Plus, 
  Trash2, 
  Calendar, 
  CheckCircle2, 
  Circle, 
  ChevronRight, 
  MessageSquare, 
  Send, 
  AtSign 
} from 'lucide-react';
import { toast } from 'sonner';
import { ICON_MAP, COLOR_MAP, STATUS_STYLES, HEALTH_STYLES } from './constants';

// ==================== FEATURE PILLAR CARD ====================
const FeaturePillarCard = ({ pillar, onClick, isAdmin, isLeadership, currentUserId }) => {
  const IconComponent = ICON_MAP[pillar.icon] || Target;
  const colors = COLOR_MAP[pillar.color] || COLOR_MAP.blue;
  const statusStyle = STATUS_STYLES[pillar.status] || STATUS_STYLES.not_started;
  const healthStyle = HEALTH_STYLES[pillar.health] || HEALTH_STYLES.green;
  const HealthIcon = healthStyle.icon;
  const completedMilestones = pillar.milestones?.filter(m => m.status === 'completed').length || 0;
  const totalMilestones = pillar.milestones?.length || 0;
  
  // Count unresolved suggestions for admin notification
  const unresolvedSuggestions = pillar.suggestions?.filter(s => !s.resolved && !s.is_admin && s.user_id !== currentUserId).length || 0;
  
  // Count suggestions with admin replies that leadership hasn't SEEN yet
  const unseenAdminReplies = pillar.suggestions?.filter(s => {
    if (!s.replies || s.replies.length === 0) return false;
    const lastReply = s.replies[s.replies.length - 1];
    if (lastReply.user_id === currentUserId) return false;
    const hasAdminReply = lastReply.is_admin || lastReply.user_role === 'super_admin' || lastReply.user_role === 'admin';
    const hasSeen = s.seen_by && s.seen_by.includes(currentUserId);
    return hasAdminReply && !hasSeen;
  }).length || 0;
  
  const adminNotifications = unresolvedSuggestions;
  const leadershipNotifications = unseenAdminReplies;

  return (
    <Card 
      className={`cursor-pointer transition-all duration-300 hover:shadow-lg hover:scale-[1.02] ${colors.border} border-2 relative`}
      onClick={() => onClick(pillar)}
    >
      {isAdmin && adminNotifications > 0 && (
        <div className="absolute -top-2 -right-2 z-10">
          <span className="flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white bg-red-500 rounded-full shadow-md animate-pulse">
            {adminNotifications}
          </span>
        </div>
      )}
      {isLeadership && !isAdmin && leadershipNotifications > 0 && (
        <div className="absolute -top-2 -right-2 z-10">
          <span className="flex items-center justify-center w-5 h-5 text-[10px] font-bold text-white bg-blue-500 rounded-full shadow-md animate-pulse">
            {leadershipNotifications}
          </span>
        </div>
      )}
      <CardHeader className="p-3 pb-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className={`p-1.5 rounded-lg ${colors.bg} flex-shrink-0`}>
              <IconComponent className={`h-4 w-4 ${colors.text}`} />
            </div>
            <div className="min-w-0 flex-1">
              <CardTitle className="text-sm font-semibold truncate">{pillar.name}</CardTitle>
            </div>
          </div>
          <HealthIcon className={`h-4 w-4 ${healthStyle.color} flex-shrink-0`} title={healthStyle.label} />
        </div>
      </CardHeader>
      <CardContent className="p-3 pt-0 space-y-2">
        <p className="text-[11px] text-muted-foreground line-clamp-1">{pillar.description || 'No description'}</p>
        <div className="flex items-center justify-between gap-2">
          <Badge variant="outline" className={`${statusStyle.color} text-[10px] px-1.5 py-0`}>{statusStyle.label}</Badge>
          <span className="text-xs font-bold">{pillar.progress_percent}%</span>
        </div>
        <Progress value={pillar.progress_percent} className="h-1.5" />
        <div className="flex items-center justify-between text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1">
            <CheckCircle2 className="h-3 w-3" /> {completedMilestones}/{totalMilestones} milestones
          </span>
          <ChevronRight className="h-3 w-3" />
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== FEATURE FEEDBACK TAB ====================
const FeatureFeedbackTab = ({ pillar, isAdmin, isLeadership, currentUser, onUpdate, newSuggestion, setNewSuggestion, handleAddSuggestion }) => {
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const scrollRef = useRef(null);
  const hasMarkedSeen = useRef(false);

  // Auto-scroll to bottom when suggestions or replies change
  useEffect(() => {
    if (scrollRef.current) {
      const scrollElement = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    }
  }, [pillar?.suggestions]);

  // Mark suggestions as seen when leadership views them
  useEffect(() => {
    if (isLeadership && !isAdmin && !hasMarkedSeen.current && pillar?.suggestions?.length > 0) {
      const userId = currentUser?.id;
      const needsUpdate = pillar.suggestions.some(s => {
        const lastReply = s.replies?.[s.replies.length - 1];
        const hasAdminReply = lastReply && (lastReply.is_admin || lastReply.user_role === 'super_admin' || lastReply.user_role === 'admin');
        return hasAdminReply && (!s.seen_by || !s.seen_by.includes(userId));
      });
      
      if (needsUpdate) {
        const updatedSuggestions = pillar.suggestions.map(s => {
          const lastReply = s.replies?.[s.replies.length - 1];
          const hasAdminReply = lastReply && (lastReply.is_admin || lastReply.user_role === 'super_admin' || lastReply.user_role === 'admin');
          if (hasAdminReply) {
            const seenBy = s.seen_by || [];
            if (!seenBy.includes(userId)) {
              return { ...s, seen_by: [...seenBy, userId] };
            }
          }
          return s;
        });
        hasMarkedSeen.current = true;
        onUpdate({ ...pillar, suggestions: updatedSuggestions });
      }
    }
  }, [isLeadership, isAdmin, pillar, currentUser, onUpdate]);

  const handleResolveSuggestion = (suggestionId) => {
    const adminName = `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'Admin';
    const updatedSuggestions = pillar.suggestions.map(s => 
      s.id === suggestionId ? { 
        ...s, 
        resolved: true, 
        resolved_at: new Date().toISOString(),
        resolved_by: adminName
      } : s
    );
    onUpdate({ ...pillar, suggestions: updatedSuggestions });
    toast.success('Suggestion resolved');
  };

  const handleAddReply = (suggestionId) => {
    if (!replyText.trim()) return;
    const userName = `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'User';
    const reply = {
      id: `reply_${Date.now()}`,
      content: replyText.trim(),
      user_name: userName,
      user_role: currentUser?.role,
      is_admin: isAdmin,
      created_at: new Date().toISOString()
    };
    const updatedSuggestions = pillar.suggestions.map(s => 
      s.id === suggestionId ? { 
        ...s, 
        replies: [...(s.replies || []), reply],
        ...(isAdmin ? { 
          resolved: true, 
          resolved_at: new Date().toISOString(),
          resolved_by: userName 
        } : {})
      } : s
    );
    onUpdate({ ...pillar, suggestions: updatedSuggestions });
    setReplyingTo(null);
    setReplyText('');
    toast.success(isAdmin ? 'Reply sent & resolved' : 'Reply sent');
  };

  const getReplyStyle = (reply) => {
    if (reply.is_admin || reply.user_role === 'super_admin' || reply.user_role === 'admin') {
      return { bg: 'bg-blue-500/10', labelColor: 'text-blue-700', label: 'Dev Team' };
    }
    return { bg: 'bg-amber-500/10', labelColor: 'text-amber-700', label: 'Leadership' };
  };

  const unresolvedCount = pillar.suggestions?.filter(s => !s.resolved).length || 0;

  return (
    <div className="space-y-4">
      <div>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs text-muted-foreground">
            {isAdmin ? 'Suggestions from Leadership' : 'Your Suggestions'}
            {isAdmin && unresolvedCount > 0 && (
              <Badge variant="destructive" className="text-[10px] ml-2">{unresolvedCount} new</Badge>
            )}
          </Label>
          {pillar.suggestions?.length > 2 && (
            <span className="text-[10px] text-muted-foreground">↕ Scroll for more</span>
          )}
        </div>
        <ScrollArea className="h-[320px] border rounded-lg p-2 bg-background/50" ref={scrollRef}>
          <div className="space-y-3 pr-4">
            {pillar.suggestions?.length > 0 ? pillar.suggestions.map((sug) => {
              const lastReply = sug.replies?.[sug.replies.length - 1];
              const hasAdminReply = lastReply && (lastReply.is_admin || lastReply.user_role === 'super_admin' || lastReply.user_role === 'admin');
              const leadershipCanReply = isLeadership && !isAdmin && hasAdminReply;
              const isUnseen = isLeadership && !isAdmin && hasAdminReply && (!sug.seen_by || !sug.seen_by.includes(currentUser?.id));
              
              let cardStyle = sug.resolved ? 'bg-muted/30 border-muted' : 'bg-amber-500/10 border-amber-500/30';
              if (isUnseen) {
                cardStyle = 'bg-blue-500/10 border-2 border-blue-500 ring-2 ring-blue-500/20';
              }
              
              return (
              <div key={sug.id} className={`p-3 rounded-lg border ${cardStyle} relative`}>
                {isUnseen && (
                  <div className="absolute -top-2 -right-2">
                    <Badge className="bg-blue-500 text-white text-[9px] px-1.5 animate-pulse">NEW</Badge>
                  </div>
                )}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Avatar className="h-7 w-7">
                      <AvatarFallback className="text-xs bg-amber-500/20 text-amber-700">
                        {sug.user_name?.split(' ').map(n => n[0]).join('') || 'L'}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <span className="text-sm font-medium">{sug.user_name}</span>
                      <span className="text-xs text-muted-foreground ml-2">
                        {sug.user_role?.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-1 items-center">
                    {sug.resolved ? (
                      <>
                        <Badge variant="secondary" className="text-[10px] bg-green-500/20 text-green-700">✓ Resolved</Badge>
                        {leadershipCanReply && (
                          <Button variant="ghost" size="sm" className="h-6 text-xs text-blue-600" onClick={() => setReplyingTo(replyingTo === sug.id ? null : sug.id)}>
                            <MessageSquare className="h-3 w-3 mr-1" /> Reply
                          </Button>
                        )}
                      </>
                    ) : (
                      <>
                        <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setReplyingTo(replyingTo === sug.id ? null : sug.id)}>
                          <MessageSquare className="h-3 w-3 mr-1" /> Reply
                        </Button>
                        {isAdmin && (
                          <Button variant="ghost" size="sm" className="h-6 text-xs text-green-600" onClick={() => handleResolveSuggestion(sug.id)}>
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Resolve
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
                
                <p className="text-sm mt-2 whitespace-pre-wrap">{sug.content}</p>
                <p className="text-xs text-muted-foreground mt-2">Sent: {new Date(sug.created_at).toLocaleString()}</p>
                
                {sug.resolved && (
                  <div className="mt-2 pt-2 border-t">
                    <p className="text-xs text-green-600">
                      ✓ Resolved by {sug.resolved_by} on {new Date(sug.resolved_at).toLocaleString()}
                    </p>
                  </div>
                )}
                
                {sug.replies?.length > 0 && (
                  <div className="mt-2 pt-2 border-t space-y-2">
                    {sug.replies.map(reply => {
                      const style = getReplyStyle(reply);
                      return (
                        <div key={reply.id} className={`${style.bg} p-2 rounded text-xs`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className={`font-medium ${style.labelColor}`}>
                              {reply.user_name} <span className="opacity-70">({style.label})</span>
                            </span>
                            <span className="text-[9px] text-muted-foreground">{new Date(reply.created_at).toLocaleString()}</span>
                          </div>
                          <p className="whitespace-pre-wrap">{reply.content}</p>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {replyingTo === sug.id && (!sug.resolved || leadershipCanReply) && (
                  <div className="mt-2 pt-2 border-t space-y-2">
                    <Textarea 
                      placeholder={isAdmin ? "Reply to this suggestion..." : "Reply to the dev team..."} 
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      rows={2}
                      className="text-xs"
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => { setReplyingTo(null); setReplyText(''); }}>
                        Cancel
                      </Button>
                      <Button size="sm" className="h-6 text-[10px]" onClick={() => handleAddReply(sug.id)} disabled={!replyText.trim()}>
                        <Send className="h-3 w-3 mr-1" /> Send
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}) : (
              <div className="text-center py-8 text-muted-foreground">
                <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{isAdmin ? 'No suggestions from leadership yet' : 'No suggestions sent yet'}</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
      
      {isLeadership && !isAdmin && (
        <>
          <Separator />
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Send a suggestion or note to the dev team</Label>
            <Textarea 
              placeholder="Describe your suggestion, feedback, or request..." 
              value={newSuggestion} 
              onChange={(e) => setNewSuggestion(e.target.value)} 
              rows={3} 
            />
            <Button size="sm" onClick={handleAddSuggestion} disabled={!newSuggestion.trim()} className="w-full">
              <Send className="h-4 w-4 mr-1" /> Send to Dev Team
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

// ==================== FEATURE DETAIL SHEET ====================
const FeatureDetailSheet = ({ pillar, open, onOpenChange, isAdmin, isLeadership, onSave, currentUser }) => {
  const [editedPillar, setEditedPillar] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [newMilestone, setNewMilestone] = useState('');
  const [newMilestoneDue, setNewMilestoneDue] = useState('');
  const [newUpdate, setNewUpdate] = useState('');
  const [newSuggestion, setNewSuggestion] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (pillar) setEditedPillar({ ...pillar });
  }, [pillar]);

  if (!editedPillar) return null;

  const colors = COLOR_MAP[editedPillar.color] || COLOR_MAP.blue;
  const IconComponent = ICON_MAP[editedPillar.icon] || Target;

  const handleAddMilestone = () => {
    if (!newMilestone.trim()) return;
    const milestone = { id: `ms_${Date.now()}`, name: newMilestone.trim(), status: 'pending', due_date: newMilestoneDue || null };
    setEditedPillar({ ...editedPillar, milestones: [...(editedPillar.milestones || []), milestone] });
    setNewMilestone(''); setNewMilestoneDue('');
  };

  const handleToggleMilestone = (ms) => {
    const updated = editedPillar.milestones.map(m => m.id === ms.id ? { ...m, status: m.status === 'completed' ? 'pending' : 'completed' } : m);
    setEditedPillar({ ...editedPillar, milestones: updated });
  };

  const handleDeleteMilestone = (ms) => {
    setEditedPillar({ ...editedPillar, milestones: editedPillar.milestones.filter(m => m.id !== ms.id) });
  };

  const handleAddUpdate = () => {
    if (!newUpdate.trim()) return;
    const mentions = [...newUpdate.matchAll(/@(\w+)/g)].map(m => m[1]);
    const update = { id: `upd_${Date.now()}`, content: newUpdate.trim(), user_name: 'You', tagged_users: mentions, created_at: new Date().toISOString() };
    setEditedPillar({ ...editedPillar, updates: [...(editedPillar.updates || []), update] });
    setNewUpdate('');
  };

  const handleAddSuggestion = async () => {
    if (!newSuggestion.trim()) return;
    const suggestion = {
      id: `sug_${Date.now()}`,
      content: newSuggestion.trim(),
      user_id: currentUser?.id,
      user_name: `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'Leadership',
      user_role: currentUser?.role,
      created_at: new Date().toISOString(),
      resolved: false
    };
    const updatedPillar = { ...editedPillar, suggestions: [...(editedPillar.suggestions || []), suggestion] };
    setEditedPillar(updatedPillar);
    setNewSuggestion('');
    try {
      await onSave(updatedPillar);
      toast.success('Suggestion sent to dev team');
    } catch (e) {
      toast.error('Failed to send suggestion');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(editedPillar);
      toast.success('Feature updated');
      setTimeout(() => onOpenChange(false), 100);
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const completedMilestones = editedPillar.milestones?.filter(m => m.status === 'completed').length || 0;
  const totalMilestones = editedPillar.milestones?.length || 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${colors.bg}`}>
              <IconComponent className={`h-5 w-5 ${colors.text}`} />
            </div>
            <div>
              <SheetTitle>{editedPillar.name}</SheetTitle>
              <SheetDescription>{editedPillar.description}</SheetDescription>
            </div>
          </div>
        </SheetHeader>
        <div className="flex gap-1 p-1 bg-muted rounded-lg mb-4">
          {['overview', 'milestones', 'updates', ...(isLeadership || isAdmin ? ['feedback'] : [])].map((tab) => (
            <button key={tab} onClick={() => setActiveTab(tab)}
              className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${activeTab === tab ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
              {tab === 'feedback' && isAdmin && editedPillar.suggestions?.some(s => !s.resolved) && (
                <span className="ml-1 inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-red-500 rounded-full">
                  {editedPillar.suggestions?.filter(s => !s.resolved).length}
                </span>
              )}
            </button>
          ))}
        </div>
        {activeTab === 'overview' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-muted-foreground">Status</Label>
                <Select value={editedPillar.status} onValueChange={(v) => setEditedPillar({...editedPillar, status: v})} disabled={!isAdmin}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_STYLES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Health</Label>
                <Select value={editedPillar.health} onValueChange={(v) => setEditedPillar({...editedPillar, health: v})} disabled={!isAdmin}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="green">On Track</SelectItem>
                    <SelectItem value="yellow">At Risk</SelectItem>
                    <SelectItem value="red">Blocked</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs text-muted-foreground">Progress</Label>
                <span className="text-sm font-bold">{editedPillar.progress_percent}%</span>
              </div>
              <input type="range" min="0" max="100" value={editedPillar.progress_percent || 0}
                onChange={(e) => setEditedPillar({...editedPillar, progress_percent: parseInt(e.target.value)})} className="w-full" disabled={!isAdmin} />
              <Progress value={editedPillar.progress_percent} className="h-2 mt-2" />
            </div>
            <div className="p-3 bg-muted/50 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Milestones</span>
                <span className="text-sm text-muted-foreground">{completedMilestones} of {totalMilestones} completed</span>
              </div>
              <Progress value={totalMilestones > 0 ? (completedMilestones / totalMilestones) * 100 : 0} className="h-1.5 mt-2" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Notes</Label>
              <Textarea value={editedPillar.notes || ''} onChange={(e) => setEditedPillar({...editedPillar, notes: e.target.value})}
                className="mt-1" rows={3} placeholder="Add internal notes..." disabled={!isAdmin} />
            </div>
          </div>
        )}
        {activeTab === 'milestones' && (
          <div className="space-y-4">
            {isAdmin && (
              <div className="p-3 bg-muted/50 rounded-lg space-y-2">
                <div className="flex gap-2">
                  <Input placeholder="New milestone..." value={newMilestone} onChange={(e) => setNewMilestone(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddMilestone()} className="flex-1" />
                  <Button size="sm" onClick={handleAddMilestone}><Plus className="h-4 w-4" /></Button>
                </div>
                <Input type="date" value={newMilestoneDue} onChange={(e) => setNewMilestoneDue(e.target.value)} className="text-sm" />
              </div>
            )}
            <ScrollArea className="h-[300px]">
              <div className="space-y-2 pr-4">
                {editedPillar.milestones?.map((ms) => (
                  <div key={ms.id} className={`flex items-center gap-3 p-2 rounded-lg ${ms.status === 'completed' ? 'bg-green-500/10' : 'bg-muted/50'}`}>
                    <button onClick={() => handleToggleMilestone(ms)} disabled={!isAdmin}>
                      {ms.status === 'completed' ? <CheckCircle2 className="h-5 w-5 text-green-500" /> : <Circle className="h-5 w-5 text-muted-foreground" />}
                    </button>
                    <div className="flex-1">
                      <p className={`text-sm ${ms.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>{ms.name}</p>
                      {ms.due_date && <p className="text-xs text-muted-foreground flex items-center gap-1"><Calendar className="h-3 w-3" />Due: {new Date(ms.due_date).toLocaleDateString()}</p>}
                    </div>
                    {isAdmin && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDeleteMilestone(ms)}><Trash2 className="h-3.5 w-3.5" /></Button>}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
        {activeTab === 'updates' && (
          <div className="space-y-4">
            {isAdmin && (
              <div className="space-y-2">
                <Textarea placeholder="Add an update... Use @name to tag users" value={newUpdate} onChange={(e) => setNewUpdate(e.target.value)} rows={3} />
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground flex items-center gap-1"><AtSign className="h-3 w-3" />Type @name to mention</p>
                  <Button size="sm" onClick={handleAddUpdate} disabled={!newUpdate.trim()}><Send className="h-4 w-4 mr-1" />Post</Button>
                </div>
              </div>
            )}
            {isAdmin && <Separator />}
            <ScrollArea className="h-[280px]">
              <div className="space-y-3 pr-4">
                {editedPillar.updates?.length > 0 ? [...editedPillar.updates].reverse().map((upd) => (
                  <div key={upd.id} className="flex gap-3 p-3 bg-muted/30 rounded-lg">
                    <Avatar className="h-8 w-8"><AvatarFallback className="text-xs">{upd.user_name?.split(' ').map(n => n[0]).join('') || 'U'}</AvatarFallback></Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">{upd.user_name}</span>
                        <span className="text-xs text-muted-foreground">{new Date(upd.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-sm mt-1 whitespace-pre-wrap">{upd.content}</p>
                      {upd.tagged_users?.length > 0 && (
                        <div className="flex gap-1 mt-1">{upd.tagged_users.map((u, i) => <Badge key={i} variant="secondary" className="text-[10px]">@{u}</Badge>)}</div>
                      )}
                    </div>
                  </div>
                )) : <div className="text-center py-8 text-muted-foreground"><MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" /><p className="text-sm">No updates yet</p></div>}
              </div>
            </ScrollArea>
          </div>
        )}
        {activeTab === 'feedback' && (isLeadership || isAdmin) && (
          <FeatureFeedbackTab 
            pillar={editedPillar} 
            isAdmin={isAdmin} 
            isLeadership={isLeadership} 
            currentUser={currentUser}
            onUpdate={(updatedPillar) => {
              setEditedPillar(updatedPillar);
              onSave(updatedPillar);
            }}
            newSuggestion={newSuggestion}
            setNewSuggestion={setNewSuggestion}
            handleAddSuggestion={handleAddSuggestion}
          />
        )}
        {isAdmin && (
          <div className="mt-6 pt-4 border-t">
            <Button onClick={handleSave} disabled={saving} className="w-full">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save Changes
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export { FeaturePillarCard, FeatureFeedbackTab, FeatureDetailSheet };
